import { ScanResult, ScanSummary, createScanSummary } from './types';
import { supabase } from '@/integrations/supabase/client';
import { getWordForDateShuffled } from '@/utils/wordlistEngine';

/**
 * Data Integrity Scan
 * Validates consistency between localStorage and cloud data
 */
export async function runDataIntegrityScan(): Promise<ScanSummary> {
  console.group('🔐 Data Integrity Scan');
  
  const results: ScanResult[] = [];

  try {
    // ========== Check 1: LocalStorage Key Inventory ==========
    const localStorageKeys = Object.keys(localStorage);
    const wordpopKeys = localStorageKeys.filter(k => k.startsWith('wordpop'));
    const gameProgressKeys = localStorageKeys.filter(k => k.startsWith('wordpop-game-'));
    
    results.push({
      name: 'LocalStorage Inventory',
      status: 'info',
      message: `${wordpopKeys.length} WordPop keys, ${gameProgressKeys.length} game progress entries`,
      details: `Total localStorage keys: ${localStorageKeys.length}`
    });

    // ========== Check 2: LocalStorage Data Format Validation ==========
    const formatIssues: string[] = [];
    
    gameProgressKeys.forEach(key => {
      try {
        const data = JSON.parse(localStorage.getItem(key) || '{}');
        
        // Validate required fields
        if (data.guesses && !Array.isArray(data.guesses)) {
          formatIssues.push(`${key}: guesses is not an array`);
        }
        if (data.solution && typeof data.solution !== 'string') {
          formatIssues.push(`${key}: solution is not a string`);
        }
        if (data.solution && data.solution.length !== 5) {
          formatIssues.push(`${key}: solution is not 5 characters`);
        }
      } catch (e) {
        formatIssues.push(`${key}: invalid JSON`);
      }
    });

    results.push({
      name: 'LocalStorage Format',
      status: formatIssues.length === 0 ? 'pass' : 'fail',
      message: formatIssues.length === 0 
        ? `All ${gameProgressKeys.length} entries have valid format`
        : `${formatIssues.length} format issue(s)`,
      details: formatIssues.length > 0 ? formatIssues.slice(0, 3).join('; ') : 'All data formats valid'
    });

    // ========== Check 3: Solution Hash Consistency ==========
    const hashIssues: string[] = [];
    let hashChecked = 0;
    
    for (const key of gameProgressKeys.slice(0, 10)) { // Sample first 10
      try {
        const data = JSON.parse(localStorage.getItem(key) || '{}');
        if (data.solution) {
          const dateMatch = key.match(/wordpop-game-(\d{4}-\d{2}-\d{2})/);
          if (dateMatch) {
            const expectedSolution = getWordForDateShuffled(new Date(dateMatch[1]));
            if (data.solution !== expectedSolution) {
              hashIssues.push(`${dateMatch[1]}: stored "${data.solution}" vs expected "${expectedSolution}"`);
            }
            hashChecked++;
          }
        }
      } catch (e) {
        // Skip invalid entries
      }
    }

    results.push({
      name: 'Solution Consistency',
      status: hashIssues.length === 0 ? 'pass' : 'warn',
      message: hashIssues.length === 0 
        ? `${hashChecked} solutions verified against wordlist`
        : `${hashIssues.length} solution mismatch(es)`,
      details: hashIssues.length > 0 ? hashIssues.slice(0, 2).join('; ') : 'All solutions match deterministic wordlist'
    });

    // ========== Check 4: Stats Integrity (Local) ==========
    const localStats = localStorage.getItem('wordpop-game-stats');
    let statsIssues: string[] = [];
    
    if (localStats) {
      try {
        const stats = JSON.parse(localStats);
        
        // Validate stat ranges
        if (stats.totalPlayed < 0) statsIssues.push('totalPlayed is negative');
        if (stats.totalWon > stats.totalPlayed) statsIssues.push('totalWon > totalPlayed');
        if (stats.currentStreak < 0) statsIssues.push('currentStreak is negative');
        if (stats.maxStreak < stats.currentStreak) statsIssues.push('maxStreak < currentStreak');
        
        // Validate guess distribution
        if (stats.guessDistribution) {
          const distSum = Object.values(stats.guessDistribution).reduce((a: number, b: any) => a + Number(b), 0);
          if (distSum !== stats.totalWon) {
            statsIssues.push(`Distribution sum (${distSum}) !== totalWon (${stats.totalWon})`);
          }
        }
      } catch (e) {
        statsIssues.push('Invalid stats JSON');
      }
    }

    results.push({
      name: 'Local Stats Integrity',
      status: statsIssues.length === 0 ? 'pass' : 'warn',
      message: statsIssues.length === 0 
        ? 'Local stats are internally consistent'
        : `${statsIssues.length} stats issue(s)`,
      details: statsIssues.length > 0 ? statsIssues.join('; ') : 'Stats pass all validation checks'
    });

    // ========== Check 5: Cloud Sync Status ==========
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user) {
      // Check cloud data exists
      const { data: cloudStats, error: statsError } = await supabase
        .from('game_stats')
        .select('*')
        .eq('user_id', user.id)
        .single();

      const { data: cloudProgress, error: progressError } = await supabase
        .from('game_progress')
        .select('puzzle_date')
        .eq('user_id', user.id);

      if (statsError && statsError.code !== 'PGRST116') {
        results.push({
          name: 'Cloud Stats Access',
          status: 'fail',
          message: 'Failed to fetch cloud stats',
          details: statsError.message
        });
      } else {
        results.push({
          name: 'Cloud Stats Access',
          status: cloudStats ? 'pass' : 'info',
          message: cloudStats 
            ? `Cloud stats: ${cloudStats.total_played || 0} played, ${cloudStats.total_won || 0} won`
            : 'No cloud stats found (new user)',
          details: cloudStats ? `Streak: ${cloudStats.current_streak || 0}` : 'Stats will sync after first game'
        });
      }

      // ========== Check 6: Cloud vs Local Comparison ==========
      if (cloudStats && localStats) {
        const local = JSON.parse(localStats);
        const syncIssues: string[] = [];
        
        if (cloudStats.total_played !== local.totalPlayed) {
          syncIssues.push(`totalPlayed: cloud=${cloudStats.total_played} vs local=${local.totalPlayed}`);
        }
        if (cloudStats.total_won !== local.totalWon) {
          syncIssues.push(`totalWon: cloud=${cloudStats.total_won} vs local=${local.totalWon}`);
        }
        if (cloudStats.current_streak !== local.currentStreak) {
          syncIssues.push(`streak: cloud=${cloudStats.current_streak} vs local=${local.currentStreak}`);
        }

        results.push({
          name: 'Cloud-Local Sync',
          status: syncIssues.length === 0 ? 'pass' : 'warn',
          message: syncIssues.length === 0 
            ? 'Cloud and local stats are in sync'
            : `${syncIssues.length} sync discrepancy(s)`,
          details: syncIssues.length > 0 ? syncIssues.slice(0, 2).join('; ') : 'Perfect sync between cloud and local'
        });
      }

      // ========== Check 7: Cloud Progress Count ==========
      if (cloudProgress) {
        results.push({
          name: 'Cloud Progress Entries',
          status: 'info',
          message: `${cloudProgress.length} game(s) synced to cloud`,
          details: cloudProgress.length > 0 
            ? `Latest: ${cloudProgress[cloudProgress.length - 1]?.puzzle_date}`
            : 'No games synced yet'
        });
      }

    } else {
      results.push({
        name: 'Cloud Sync Status',
        status: 'info',
        message: 'User not authenticated - cloud sync unavailable',
        details: 'Sign in to enable cloud sync validation'
      });
    }

    // ========== Check 8: Orphaned Data Detection ==========
    const orphanedKeys: string[] = [];
    
    localStorageKeys.forEach(key => {
      // Check for old/deprecated keys (wormble- prefix that wasn't migrated)
      if (key.includes('wormble') && !key.includes('wordpop')) {
        orphanedKeys.push(key);
      }
      // Check for corrupted date formats
      if (key.startsWith('wordpop-game-') && !key.match(/wordpop-game-\d{4}-\d{2}-\d{2}/)) {
        orphanedKeys.push(key);
      }
    });

    results.push({
      name: 'Orphaned Data Check',
      status: orphanedKeys.length === 0 ? 'pass' : 'warn',
      message: orphanedKeys.length === 0 
        ? 'No orphaned or deprecated data found'
        : `${orphanedKeys.length} orphaned key(s)`,
      details: orphanedKeys.length > 0 ? orphanedKeys.slice(0, 3).join('; ') : 'All data is current'
    });

    // ========== Check 9: LocalStorage Size ==========
    let totalSize = 0;
    for (const key of localStorageKeys) {
      totalSize += (localStorage.getItem(key) || '').length * 2; // UTF-16
    }
    const sizeKB = (totalSize / 1024).toFixed(2);
    const sizePercent = ((totalSize / (5 * 1024 * 1024)) * 100).toFixed(1);

    results.push({
      name: 'LocalStorage Usage',
      status: totalSize < 4 * 1024 * 1024 ? 'pass' : 'warn',
      message: `${sizeKB} KB used (${sizePercent}% of 5MB limit)`,
      details: totalSize > 4 * 1024 * 1024 ? 'Consider cleaning old data' : 'Plenty of storage available'
    });

    // ========== Check 10: Migration Flags Status ==========
    const migrationFlags = [
      'wordpop-cloud-migrated',
      'wordpop-stale-data-cleaned-v1',
      'wordpop-stats-last-check',
      'wordpop-skip-auth',
      'wordpop-migration-complete'
    ];
    
    const flagStatus = migrationFlags.map(flag => {
      const value = localStorage.getItem(flag);
      return `${flag.replace('wordpop-', '')}: ${value ? '✓' : '✗'}`;
    });

    results.push({
      name: 'Migration Flags',
      status: 'info',
      message: `${migrationFlags.filter(f => localStorage.getItem(f)).length}/${migrationFlags.length} flags set`,
      details: flagStatus.join(', ')
    });

  } catch (error) {
    results.push({
      name: 'Data Integrity Scan Error',
      status: 'fail',
      message: `Scan failed: ${error}`,
      details: String(error)
    });
  }

  const summary = createScanSummary('Data Integrity Scan', results);
  
  console.log(`✅ Passed: ${summary.passCount}`);
  console.log(`⚠️  Warnings: ${summary.warnCount}`);
  console.log(`❌ Failed: ${summary.failCount}`);
  console.table(results);
  console.groupEnd();
  
  return summary;
}
